import { jsx as _jsx } from "react/jsx-runtime";
import { MARKETABLE_CONTACTS_WRITE } from 'pricing-pages-lib/constants/Scopes';
import { getQueryParams, stringifyQueryParams } from 'pricing-pages-lib/utils/queryParamUtils';
import { getPricingPageProductRoute } from 'pricing-pages-lib/utils/urls';
import { useHistory } from 'react-router-dom';
import { MARKETING } from 'self-service-api/constants/ProductLines';
import UIAdminOnlyWrapper from 'ui-addon-upgrades/decorators/UIAdminOnlyWrapper';
import { REQUEST_MC_WRITE } from 'ui-addon-upgrades/_core/common/constants/AdminModalTypes';
import PricingPageInterstitialBuyContactsButton from 'ui-addon-upgrades/_core/interstitial/PricingPageInterstitialBuyContactsButton';
import PricingPageInterstitialBuyNowButton from 'ui-addon-upgrades/_core/interstitial/PricingPageInterstitialBuyNowButton';
import PricingPageInterstitialBuySeatsButton from 'ui-addon-upgrades/_core/interstitial/PricingPageInterstitialBuySeatsButton';
import { BUY_ADDITIONAL_CONTACTS, BUY_ADDITIONAL_SEATS, BUY_TOUCHLESS, CALCULATE_PRICE, CALCULATE_CONTACTS, CALCULATE_SEATS } from 'ui-addon-upgrades/_core/purchaseMotions/PurchaseMotionTypes';
import { useAppSelector } from '../../../reducers';
import { isAppAuthSelector } from '../../../selectors/AuthSelectors';
import { shouldShowEligibilityFlowSelector } from '../../../selectors/MarketingContactsSelectors';
import { configsWithSuiteIfPossibleSelector, productsAndPacksForQueryParamsSelector, sortedProductConfigurationsSelector } from '../../../selectors/ProductConfigsSelectors';
import { ownsCrmSuiteProductLevel } from '../../../selectors/ProductInventorySelectors';
import { bundledProductLinesForSkuIdSelector, isCoreSeatSkuSelector, isQuantifiableHubSelector, productForSkuIdSelector, skuIdIsQuantityPackSelector } from '../../../selectors/ProductsSelectors';

const InterstitialPurchaseMotionButtonWrapper = props => {
  const {
    purchaseMotions,
    level,
    nonMerchandiseIdIdentifier,
    productConfigurations = []
  } = props;
  const sortedProductConfigurations = useAppSelector(state => sortedProductConfigurationsSelector(state, {
    productConfigurations
  }));
  const productConfiguration = sortedProductConfigurations[0];
  const product = useAppSelector(state => productForSkuIdSelector(state, {
    skuId: productConfiguration.skuId
  }));
  const {
    apiName
  } = product;
  const history = useHistory();
  const purchaseMotion = purchaseMotions[level - 1];
  const isAppAuth = useAppSelector(isAppAuthSelector);
  const queryParams = getQueryParams();
  const hasMarketingProduct = useAppSelector(state => {
    const marketingConfig = productConfigurations && productConfigurations.find(({
      skuId
    }) => {
      const bundledProductLines = bundledProductLinesForSkuIdSelector(state, {
        skuId
      });
      return [product.productLine, ...bundledProductLines].includes(MARKETING);
    });
    return marketingConfig && productForSkuIdSelector(state, {
      skuId: marketingConfig.skuId
    });
  });
  const productConfigurationsWithSuiteIfNecessary = useAppSelector(state => {
    // keep only seat configs for buying additional seats
    if (purchaseMotion === BUY_ADDITIONAL_SEATS) {
      return productConfigurations.filter(config => isCoreSeatSkuSelector(state, {
        skuId: config.skuId
      }) || isQuantifiableHubSelector(state, {
        skuId: config.skuId
      }) || skuIdIsQuantityPackSelector(state, {
        skuId: config.skuId
      }) && config.quantity > 0);
    } // Temporary workaround - you cannot buy a net new child item without including
    // the parent item. In the case of marketing contacts, if you own suite,
    // productConfigurations needs to contain the rest of the suite products
    // to take to checkout. This can be removed when upgrade-catalog can
    // handle quantity increases and net new upgrades together.


    if (purchaseMotion === BUY_ADDITIONAL_CONTACTS || hasMarketingProduct) {
      const {
        productLevel
      } = hasMarketingProduct;
      const ownedSuiteProductLevel = ownsCrmSuiteProductLevel(state, {
        productLevel: productLevel
      });

      if (ownedSuiteProductLevel) {
        return configsWithSuiteIfPossibleSelector(state, {
          productConfigurations
        });
      }
    }

    return productConfigurations;
  });
  const productConfigurationsAsQueryParams = useAppSelector(state => productsAndPacksForQueryParamsSelector(state, {
    productConfigurations: productConfigurationsWithSuiteIfNecessary
  }));
  const shouldShowEligibilityFlow = useAppSelector(shouldShowEligibilityFlowSelector); // On hub pages, the query params are set to starter by default
  // this updates the apiNames to pro if you clicked Buy Now on a pro merch card

  const adjustedQueryParams = Object.assign({}, queryParams, {}, productConfigurationsAsQueryParams);

  const onClickInterstitial = params => {
    const pricingPageRoute = getPricingPageProductRoute(isAppAuth);
    const stringifiedParams = stringifyQueryParams(Object.assign({}, params, {
      pricingPageProductRoute: pricingPageRoute || ''
    }));
    history.push(`/interstitial?${stringifiedParams}`);
  };

  const seleniumId = nonMerchandiseIdIdentifier || apiName;
  const propsToPass = Object.assign({}, props, {
    seleniumId
  });

  if (purchaseMotion === BUY_TOUCHLESS || purchaseMotion === CALCULATE_PRICE) {
    const onClick = () => onClickInterstitial(adjustedQueryParams);

    if (hasMarketingProduct && shouldShowEligibilityFlow) {
      return /*#__PURE__*/_jsx(UIAdminOnlyWrapper, {
        adminType: MARKETABLE_CONTACTS_WRITE,
        modalType: REQUEST_MC_WRITE,
        upgradeData: props.upgradeData,
        children: /*#__PURE__*/_jsx(PricingPageInterstitialBuyNowButton, Object.assign({}, propsToPass, {
          onClick: onClick
        }))
      });
    }

    return /*#__PURE__*/_jsx(PricingPageInterstitialBuyNowButton, Object.assign({}, propsToPass, {
      onClick: onClick
    }));
  }

  if (purchaseMotion === BUY_ADDITIONAL_CONTACTS || purchaseMotion === CALCULATE_CONTACTS) {
    const onClick = () => onClickInterstitial(adjustedQueryParams);

    if (shouldShowEligibilityFlow) {
      return /*#__PURE__*/_jsx(UIAdminOnlyWrapper, {
        adminType: MARKETABLE_CONTACTS_WRITE,
        modalType: REQUEST_MC_WRITE,
        upgradeData: props.upgradeData,
        children: /*#__PURE__*/_jsx(PricingPageInterstitialBuyContactsButton, Object.assign({}, propsToPass, {
          onClick: onClick
        }))
      });
    }

    return /*#__PURE__*/_jsx(PricingPageInterstitialBuyContactsButton, Object.assign({}, propsToPass, {
      onClick: onClick
    }));
  }

  if (purchaseMotion === BUY_ADDITIONAL_SEATS || purchaseMotion === CALCULATE_SEATS) {
    const onClick = () => onClickInterstitial(adjustedQueryParams);

    return /*#__PURE__*/_jsx(PricingPageInterstitialBuySeatsButton, Object.assign({}, propsToPass, {
      onClick: onClick
    }));
  }

  return null;
};

export default InterstitialPurchaseMotionButtonWrapper;